
import { getChannelContentKey, normalizeContentChannel } from '../shared/channel/contentChannel';

// Configuration for API URL
// In development, this points to the local Node.js server.
// In production, this should point to your AWS Backend URL.
const normalizeApiUrl = (input) => {
    const fallback = '/api';
    const v = (input ?? '').toString().trim();
    if (!v) return fallback;

    // Absolute URL (custom domain / ALB / API Gateway)
    if (v.startsWith('http://') || v.startsWith('https://')) {
        return v.replace(/\/+$/, '');
    }

    // Relative base: make sure it starts with "/" so it doesn't become "/admin/api" on nested routes.
    const withSlash = v.startsWith('/') ? v : `/${v}`;
    return withSlash.replace(/\/+$/, '');
};

const API_URL = normalizeApiUrl(process.env.REACT_APP_API_URL);

const withChannelQuery = (path, channel) => {
    const normalizedChannel = normalizeContentChannel(channel);
    if (!normalizedChannel) return path;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}channel=${encodeURIComponent(normalizedChannel)}`;
};

// Helper to handle response
const handleResponse = async (response, errorMessage) => {
    if (!response.ok) {
        let errorBody;
        try {
            errorBody = await response.text();
            // Try parsing JSON if possible
            try { errorBody = JSON.parse(errorBody).error || errorBody; } catch (e) { }
        } catch (e) {
            errorBody = response.statusText;
        }
        throw new Error(`${errorMessage}: ${errorBody} (${response.status})`);
    }
    const text = await response.text();
    try {
        return JSON.parse(text);
    } catch (e) {
        // CloudFront/S3 SPA fallback (index.html) can mask API 404s when the API base is wrong.
        const isHtml = /^\s*</.test(text) && /<!doctype html|<html/i.test(text);
        console.error("JSON Parse Error. Body:", text);
        throw new Error(
            isHtml
                ? `API retornou HTML (Status ${response.status}). Provavel rota/base de API incorreta. Base atual: "${API_URL}".`
                : `Invalid JSON response (Status ${response.status}): ${text.substring(0, 100)}...`
        );
    }
};

export const getNews = async (channel) => {
    const response = await fetch(`${API_URL}${withChannelQuery('/news', channel)}`);
    return handleResponse(response, 'Failed to fetch news');
};

export const createNews = async (newsItem) => {
    const response = await fetch(`${API_URL}/news`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newsItem),
    });
    return handleResponse(response, 'Failed to create news');
};

export const updateNews = async (id, newsItem) => {
    const response = await fetch(`${API_URL}/news/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newsItem),
    });
    return handleResponse(response, 'Failed to update news');
};

export const deleteNews = async (id) => {
    const response = await fetch(`${API_URL}/news/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete news');
    return true;
};

export const reorderNews = async (ids) => {
    const response = await fetch(`${API_URL}/news/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: ids }),
    });
    return handleResponse(response, 'Failed to reorder news');
};

// --- PageContent (Singleton Configs) ---

export const getPageContent = async (key) => {
    const response = await fetch(`${API_URL}/content/${key}`);
    return handleResponse(response, 'Failed to fetch content');
};

export const savePageContent = async (key, content) => {
    const response = await fetch(`${API_URL}/content/${key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content),
    });
    return handleResponse(response, 'Failed to save content');
};

const buildChannelCandidateKeys = (baseKey, channel, fallbackToLegacy) => {
    const normalizedChannel = normalizeContentChannel(channel);
    if (!normalizedChannel) return [baseKey];

    const channelKey = getChannelContentKey(baseKey, normalizedChannel);
    if (!fallbackToLegacy || channelKey === baseKey) {
        return [channelKey];
    }

    return [channelKey, baseKey];
};

export const getChannelPageContent = async (baseKey, channel, options = {}) => {
    const fallbackToLegacy = options.fallbackToLegacy !== false;
    const candidateKeys = buildChannelCandidateKeys(baseKey, channel, fallbackToLegacy);

    for (const key of candidateKeys) {
        const content = await getPageContent(key);
        if (content !== null && content !== undefined) {
            return { key, content };
        }
    }

    return { key: candidateKeys[0], content: null };
};

export const saveChannelPageContent = async (baseKey, channel, content) => {
    const key = getChannelContentKey(baseKey, channel);
    const savedContent = await savePageContent(key, content);
    return { key, content: savedContent };
};

// --- Impact Cases ---

export const getImpactCases = async () => {
    const response = await fetch(`${API_URL}/impact-cases`);
    return handleResponse(response, 'Failed to fetch cases');
};

export const addImpactCase = async (data) => {
    const response = await fetch(`${API_URL}/impact-cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to add case');
};

export const updateImpactCase = async (id, data) => {
    const response = await fetch(`${API_URL}/impact-cases/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to update case');
};

export const deleteImpactCase = async (id) => {
    const response = await fetch(`${API_URL}/impact-cases/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete case');
};

// --- Site Sections ---

export const getSiteSections = async () => {
    const response = await fetch(`${API_URL}/site-sections`);
    return handleResponse(response, 'Failed to fetch sections');
};

export const getSiteSectionBySlug = async (slug) => {
    const response = await fetch(`${API_URL}/site-sections/${slug}`);
    return handleResponse(response, 'Failed to fetch article');
};

export const addSiteSection = async (data) => {
    const response = await fetch(`${API_URL}/site-sections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to add section');
};

export const updateSiteSection = async (id, data) => {
    const response = await fetch(`${API_URL}/site-sections/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to update section');
};

export const deleteSiteSection = async (id) => {
    const response = await fetch(`${API_URL}/site-sections/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete section');
};

// --- Legal Documents ---

export const getLegalDocuments = async () => {
    const response = await fetch(`${API_URL}/legal-documents`);
    return handleResponse(response, 'Failed to fetch legal documents');
};

export const createLegalDocument = async (data) => {
    const response = await fetch(`${API_URL}/legal-documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to create legal document');
};

export const updateLegalDocument = async (id, data) => {
    const response = await fetch(`${API_URL}/legal-documents/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to update legal document');
};

export const deleteLegalDocument = async (id) => {
    const response = await fetch(`${API_URL}/legal-documents/${id}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete legal document');
};

// --- Internal Analytics (Admin) ---

export const getInternalAnalyticsOverview = async (days = 7, options = {}) => {
    const safeDays = Number.isFinite(Number(days)) ? Number(days) : 7;
    const safePage = Number.isFinite(Number(options?.page)) ? Math.max(1, Number(options.page)) : 1;
    const safePageSize = Number.isFinite(Number(options?.pageSize)) ? Math.max(1, Number(options.pageSize)) : 25;
    const params = new URLSearchParams({
        days: String(safeDays),
        page: String(safePage),
        pageSize: String(safePageSize),
    });

    const optionalFilters = {
        eventName: options?.eventName,
        source: options?.source,
        countryCode: options?.countryCode,
        search: options?.search,
        sortBy: options?.sortBy,
        sortDir: options?.sortDir,
        includeOperational: options?.includeOperational,
    };

    Object.entries(optionalFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            params.set(key, String(value).trim());
        }
    });

    const response = await fetch(`${API_URL}/admin/internal-analytics/overview?${params.toString()}`);
    return handleResponse(response, 'Failed to fetch internal analytics overview');
};

export const getSeoHealthOverview = async () => {
    const response = await fetch(`${API_URL}/admin/seo-health/overview`);
    return handleResponse(response, 'Failed to fetch seo health overview');
};

export const getContactRoutingConfig = async () => {
    const response = await fetch(`${API_URL}/admin/contact-routing-config`);
    return handleResponse(response, 'Failed to fetch contact routing config');
};

export const updateContactRoutingConfig = async (payload) => {
    const response = await fetch(`${API_URL}/admin/contact-routing-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    return handleResponse(response, 'Failed to update contact routing config');
};

// --- Contact ---

export const sendContactMessage = async (data) => {
    const response = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    return handleResponse(response, 'Failed to send message');
};
